/**
 * Business › List on a phone (SAM-1308, canvas BusinessesMobile): a title with the count, a full-width search, the status
 * capsules, and a card per business: its logo whole (never cropped), name, category and hours, and a status pill.
 * View only on a phone (SAM-944 M5): no «New business». Presentational: BusinessList.tsx owns the data and the filters.
 */
import React from 'react';
import { Building2 } from 'lucide-react';
import { PhoneListState } from '../components/phone/PhoneCards';
import {
  PhoneCapsules, PhoneItemList, PhoneLogo, PhonePage, PhoneSearch, PhoneTitleRow, type PhoneItem,
} from '../components/phone/PhoneListParts';
import { S3Image } from '../components/S3Image';
import { useT } from '../i18n/admin';

export type BusinessStatusFilter = 'all' | 'active' | 'inactive';

export interface PhoneBusiness {
  id: string;
  name: string;
  logoKey?: string | null;
  category?: string | null;
  /** «Open · until 6 PM» / «Closed · opens 9 AM», when the business has a schedule. */
  hours?: string | null;
  active: boolean;
}

interface Props {
  businesses: PhoneBusiness[];
  loading: boolean;
  count: number;
  query: string;
  onQuery: (q: string) => void;
  filter: BusinessStatusFilter;
  onFilter: (f: BusinessStatusFilter) => void;
  onOpen: (id: string) => void;
}

export const BusinessListPhone: React.FC<Props> = ({ businesses, loading, count, query, onQuery, filter, onFilter, onOpen }) => {
  const { t } = useT();
  const items: PhoneItem[] = businesses.map((b) => ({
    key: b.id,
    title: b.name,
    sub: b.category || undefined,
    extra: b.hours || undefined,
    avatar: (
      <PhoneLogo fallback={<Building2 size={22} className="text-[var(--ink-400)]" aria-hidden />}>
        {b.logoKey ? <S3Image imageKey={b.logoKey} alt="" /> : undefined}
      </PhoneLogo>
    ),
    status: b.active ? { label: t('phoneLists.business.active'), tone: 'ok' } : { label: t('phoneLists.business.inactive'), tone: 'off' },
    open: () => onOpen(b.id),
  }));
  const chips = (['all', 'active', 'inactive'] as const).map((id) => ({ id, label: t(`phoneLists.business.${id}`), on: filter === id }));
  return (
    <PhonePage>
      <PhoneTitleRow title={t('phoneLists.business.title')} summary={loading ? undefined : t('phoneLists.business.summary', { n: count.toLocaleString('en-US') })} />
      <PhoneSearch placeholder={t('phoneLists.business.search')} value={query} onChange={onQuery} />
      <PhoneCapsules ariaLabel={t('phoneLists.business.filterLabel')} chips={chips} onPick={(id) => onFilter(id as BusinessStatusFilter)} />
      <PhoneListState loading={loading} failed={false} empty={!loading && items.length === 0} emptyText={t('phoneLists.business.empty')} />
      {!loading && items.length > 0 && <PhoneItemList items={items} />}
    </PhonePage>
  );
};
